import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
    Box, Typography, Stack, Button, IconButton, TextField, InputAdornment,
    Select, MenuItem, FormControl, InputLabel, FormHelperText,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Tooltip, Paper, Pagination,
    Skeleton, Avatar, useMediaQuery,
} from "@mui/material";
import {
    Add, Search, Edit, Delete, Sync, ContactPhone, Close,
    ErrorOutline, Person, PhoneForwarded,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import { toast } from "react-toastify";

/* ── Design tokens ───────────────────────────────────────────────────────── */
const C = {
    indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
    green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
    red: "#dc2626", redSoft: "#fef2f2", redBd: "#fecaca",
    amber: "#d97706", amberSoft: "#fffbeb", amberBd: "#fde68a",
    page: "#f8fafc", white: "#ffffff", border: "#e2e8f0", border2: "#f1f5f9",
    txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
    sans: "'Geist', system-ui, sans-serif",
};

const API_BASE = `${process.env.REACT_APP_PAYMENT_LINK_URL}api/calllog`;
const PAGE_SIZE = 20;

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const fmtDate = ms => {
    if (!ms) return "—";
    return new Date(typeof ms === "number" ? ms : ms).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const AV_COLORS = ["#6366f1","#0ea5e9","#8b5cf6","#ec4899","#f59e0b","#22c55e","#ef4444"];
const avColor = n => { if (!n) return C.txt4; let h = 0; for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff; return AV_COLORS[Math.abs(h) % AV_COLORS.length]; };
const initials = n => { if (!n) return "?"; const p = n.trim().split(" "); return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase(); };

const NAME_RE = /^[a-zA-Z\s.]+$/;
const PHONE_RE = /^\+?[\d\s\-]{10,15}$/;

/* ── Styled ──────────────────────────────────────────────────────────────── */
const SearchBox = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        background: C.white, borderRadius: 8, fontSize: 13.5, fontFamily: C.sans,
        "& fieldset": { borderColor: C.border },
        "&:hover fieldset": { borderColor: C.indigoBd },
        "&.Mui-focused fieldset": { borderColor: C.indigo },
        "& input::placeholder": { color: C.txt4, opacity: 1 },
    },
});

const StyledSelect = styled(Select)({
    fontFamily: C.sans, fontSize: 13.5,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.indigoBd },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: C.indigo },
});

const AddBtn = styled(Button)({
    borderRadius: 8, textTransform: "none", fontFamily: `${C.sans} !important`,
    fontWeight: 600, fontSize: 13, padding: "7px 16px",
    background: C.indigo, color: "#fff", whiteSpace: "nowrap",
    "&:hover": { background: C.indigo2, boxShadow: `0 4px 14px ${alpha(C.indigo, .3)}` },
});

const ResyncBtn = styled(Button)({
    borderRadius: 6, textTransform: "none", fontFamily: `${C.sans} !important`,
    fontWeight: 600, fontSize: 11, padding: "2px 10px",
    background: C.amberSoft, color: C.amber, border: `1px solid ${C.amberBd}`,
    whiteSpace: "nowrap", minWidth: 0,
    "&:hover": { background: C.amber, color: "#fff" },
    "&:disabled": { opacity: 0.6 },
});

const CallBtn = styled(Button)({
    borderRadius: 6, textTransform: "none", fontFamily: `${C.sans} !important`,
    fontWeight: 600, fontSize: 11, padding: "2px 10px",
    background: C.greenSoft, color: C.green, border: `1px solid ${C.greenBd}`,
    whiteSpace: "nowrap", minWidth: 0,
    "&:hover": { background: C.green, color: "#fff" },
});

/* ── Validation ──────────────────────────────────────────────────────────── */
function validateForm(data) {
    const errs = {};
    const name = data.contactName?.trim() || "";
    if (!name || name.length < 2 || name.length > 150) {
        errs.contactName = "Name must be 2–150 characters";
    } else if (!NAME_RE.test(name)) {
        errs.contactName = "Letters, spaces, and dots only";
    }
    const phone = data.contactNumber?.trim() || "";
    if (!phone) {
        errs.contactNumber = "Contact number is required";
    } else if (!PHONE_RE.test(phone)) {
        errs.contactNumber = "Enter a valid phone number (10–15 digits)";
    }
    if (!data.contactTypeId) {
        errs.contactTypeId = "Contact type is required";
    }
    return errs;
}

/* ── Add/Edit modal ──────────────────────────────────────────────────────── */
function ContactModal({ open, mode, initialData, contactTypes, onClose, onSaved, userId }) {
    const [form, setForm] = useState({ contactName: "", contactNumber: "", contactTypeId: "" });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(initialData || { contactName: "", contactNumber: "", contactTypeId: "" });
            setErrors({});
        }
    }, [open, initialData]);

    const set = field => e => setForm(p => ({ ...p, [field]: e.target.value }));

    const handleSave = async () => {
        const errs = validateForm(form);
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        try {
            const payload = {
                contactName: form.contactName.trim(),
                contactNumber: form.contactNumber.trim(),
                contactTypeId: form.contactTypeId,
                userId,
            };
            const res = mode === "add"
                ? await axios.post(`${API_BASE}/contacts`, payload)
                : await axios.put(`${API_BASE}/contacts/${initialData.contactId}`, payload);

            if (res.data?.success) {
                if (res.data?.warning) toast.warning(res.data.warning);
                else toast.success(mode === "add" ? "Contact created successfully." : "Contact updated successfully.");
                onSaved();
            } else {
                toast.error(res.data?.error || "Failed to save contact.");
            }
        } catch (e) {
            if (e.response?.status === 409) {
                setErrors({ contactNumber: e.response.data?.error || "A contact with this number already exists." });
            } else {
                toast.error("Failed to save contact. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: `1px solid ${C.border}`, fontFamily: C.sans } }}>
            <DialogTitle sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}` }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", background: C.indigoSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Person sx={{ fontSize: 16, color: C.indigo }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.txt1, fontFamily: C.sans }}>
                        {mode === "add" ? "Add Contact" : "Edit Contact"}
                    </Typography>
                </Stack>
                <IconButton size="small" onClick={onClose} sx={{ color: C.txt4 }}>
                    <Close sx={{ fontSize: 16 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1, background: C.page }}>
                <Stack spacing={2}>
                    <TextField label="Contact Name" size="small" fullWidth value={form.contactName}
                        onChange={set("contactName")} error={!!errors.contactName} helperText={errors.contactName}
                        inputProps={{ maxLength: 150 }}
                        sx={{ "& .MuiOutlinedInput-root": { fontFamily: C.sans, fontSize: 13.5 }, "& label": { fontFamily: C.sans } }} />
                    <TextField label="Contact Number" size="small" fullWidth value={form.contactNumber}
                        onChange={set("contactNumber")} error={!!errors.contactNumber}
                        helperText={errors.contactNumber || "E.g. +919812345678"} placeholder="+919812345678"
                        inputProps={{ maxLength: 20 }}
                        sx={{ "& .MuiOutlinedInput-root": { fontFamily: C.sans, fontSize: 13.5 }, "& label": { fontFamily: C.sans } }} />
                    <FormControl size="small" fullWidth error={!!errors.contactTypeId}>
                        <InputLabel sx={{ fontFamily: C.sans }}>Contact Type</InputLabel>
                        <Select label="Contact Type" value={form.contactTypeId} onChange={set("contactTypeId")} sx={{ fontFamily: C.sans, fontSize: 13.5 }}>
                            {contactTypes.map(ct => (
                                <MenuItem key={ct.contact_type_id} value={ct.contact_type_id} sx={{ fontFamily: C.sans, fontSize: 13.5 }}>
                                    {ct.contact_type}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.contactTypeId && <FormHelperText>{errors.contactTypeId}</FormHelperText>}
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 2, borderTop: `1px solid ${C.border}`, background: C.page, gap: 1 }}>
                <Button onClick={onClose} disabled={saving}
                    sx={{ borderRadius: 7, textTransform: "none", fontFamily: C.sans, fontWeight: 500, fontSize: 13, color: C.txt3, border: `1px solid ${C.border}`, px: 2 }}>
                    Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} variant="contained"
                    sx={{ borderRadius: 7, textTransform: "none", fontFamily: C.sans, fontWeight: 600, fontSize: 13, background: C.indigo, px: 2.5, "&:hover": { background: C.indigo2 } }}>
                    {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : (mode === "add" ? "Save Contact" : "Update Contact")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ── Delete confirm dialog ───────────────────────────────────────────────── */
function DeleteDialog({ open, onClose, onConfirm, deleting }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: `1px solid ${C.border}` } }}>
            <DialogTitle sx={{ px: 2.5, py: 2, fontFamily: C.sans, fontWeight: 700, fontSize: 15, color: C.txt1, borderBottom: `1px solid ${C.border}` }}>
                Delete Contact?
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
                <Typography sx={{ fontSize: 13.5, color: C.txt2, fontFamily: C.sans }}>
                    This contact will be permanently removed. This action cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 2, borderTop: `1px solid ${C.border}`, gap: 1 }}>
                <Button onClick={onClose} disabled={deleting}
                    sx={{ borderRadius: 7, textTransform: "none", fontFamily: C.sans, fontWeight: 500, fontSize: 13, color: C.txt3, border: `1px solid ${C.border}`, px: 2 }}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} disabled={deleting} variant="contained"
                    sx={{ borderRadius: 7, textTransform: "none", fontFamily: C.sans, fontWeight: 600, fontSize: 13, background: C.red, px: 2.5, "&:hover": { background: "#b91c1c" } }}>
                    {deleting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ── Mobile card ─────────────────────────────────────────────────────────── */
function ContactCard({ c, onCall, onEdit, onDelete, onResync, resyncing }) {
    const name = c.contact_name ?? c.contactName ?? "—";
    const number = c.contact_number ?? c.contactNumber ?? "";
    const typeName = c.ContactType?.contact_type ?? c.contactType ?? "—";
    const synced = c.is_contact_sync ?? c.isContactSync ?? false;
    const createdAt = c.created_at ?? c.createdAt;

    return (
        <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.indigo}`, borderRadius: 2, p: 1.5, mb: 1.25 }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Avatar sx={{ width: 36, height: 36, background: avColor(name), fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {initials(name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.txt1, fontFamily: C.sans }}>
                            {name}
                        </Typography>
                        <Box sx={{ display: "inline-flex", px: 1, py: 0.2, borderRadius: 6, background: C.indigoSoft, border: `1px solid ${C.indigoBd}`, fontSize: 11, fontWeight: 600, color: C.indigo, fontFamily: C.sans }}>
                            {typeName}
                        </Box>
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: C.txt4, fontFamily: C.sans, mb: 1 }}>
                        {fmtDate(createdAt)}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5}>
                        <CallBtn startIcon={<PhoneForwarded sx={{ fontSize: 12 }} />}
                            onClick={() => onCall({ leadDisplayId: "" }, { name, contactNumber: number })}>
                            Call
                        </CallBtn>
                        <IconButton size="small" onClick={() => onEdit(c)}
                            sx={{ color: C.indigo, background: C.indigoSoft, border: `1px solid ${C.indigoBd}`, borderRadius: 6, width: 28, height: 28, "&:hover": { background: C.indigo, color: "#fff" } }}>
                            <Edit sx={{ fontSize: 13 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(c)}
                            sx={{ color: C.red, background: C.redSoft, border: `1px solid ${C.redBd}`, borderRadius: 6, width: 28, height: 28, "&:hover": { background: C.red, color: "#fff" } }}>
                            <Delete sx={{ fontSize: 13 }} />
                        </IconButton>
                        {!synced && (
                            <ResyncBtn disabled={resyncing}
                                startIcon={resyncing ? <CircularProgress size={10} sx={{ color: C.amber }} /> : <Sync sx={{ fontSize: 12 }} />}
                                onClick={() => onResync(c)}>
                                Resync
                            </ResyncBtn>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

/* ── ContactsTab ─────────────────────────────────────────────────────────── */
export default function ContactsTab({ userId, onCallNow }) {
    const [contactTypes, setContactTypes] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");

    const [modal, setModal] = useState({ open: false, mode: "add", initialData: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, contactId: null });
    const [deleting, setDeleting] = useState(false);
    const [resyncingIds, setResyncingIds] = useState(new Set());

    const isMobile = useMediaQuery("(max-width:640px)");

    /* load contact types once */
    useEffect(() => {
        axios.get(`${API_BASE}/contact-types`)
            .then(r => { if (r.data?.success) setContactTypes(r.data.data || []); })
            .catch(() => {});
    }, []);

    /* debounce search */
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    /* fetch contacts */
    const fetchContacts = useCallback(async (pg, sv, typeId) => {
        setLoading(true);
        setLoadError(null);
        try {
            const params = { page: pg, pageSize: PAGE_SIZE };
            if (sv) params.search = sv;
            if (typeId) params.contactTypeId = typeId;
            const { data } = await axios.get(`${API_BASE}/contacts`, { params });
            if (data?.success) {
                setContacts(data.data || []);
                setTotal(data.meta?.total || 0);
            } else {
                setLoadError("Failed to load contacts.");
            }
        } catch {
            setLoadError("Failed to load contacts. Check your connection.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { setPage(1); fetchContacts(1, search, typeFilter); }, [search, typeFilter, fetchContacts]);

    const handlePageChange = (_, newPage) => { setPage(newPage); fetchContacts(newPage, search, typeFilter); };
    const refresh = () => fetchContacts(page, search, typeFilter);

    /* modal helpers */
    const openAdd = () => setModal({ open: true, mode: "add", initialData: null });
    const openEdit = c => setModal({
        open: true, mode: "edit",
        initialData: {
            contactId: c.contact_id ?? c.contactId,
            contactName: c.contact_name ?? c.contactName ?? "",
            contactNumber: c.contact_number ?? c.contactNumber ?? "",
            contactTypeId: c.contact_type_id ?? c.ContactType?.contact_type_id ?? c.contactTypeId ?? "",
        },
    });
    const handleSaved = () => { setModal({ open: false, mode: "add", initialData: null }); fetchContacts(page, search, typeFilter); };

    /* delete */
    const confirmDelete = c => setDeleteDialog({ open: true, contactId: c.contact_id ?? c.contactId });
    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API_BASE}/contacts/${deleteDialog.contactId}`);
            toast.success("Contact deleted.");
            setDeleteDialog({ open: false, contactId: null });
            fetchContacts(page, search, typeFilter);
        } catch { toast.error("Failed to delete contact."); }
        finally { setDeleting(false); }
    };

    /* resync */
    const handleResync = async c => {
        const id = c.contact_id ?? c.contactId;
        setResyncingIds(prev => new Set(prev).add(id));
        try {
            const { data } = await axios.post(`${API_BASE}/contacts/${id}/resync-tata`, { userId });
            if (data?.success) { toast.success("Contact synced to TATA successfully."); fetchContacts(page, search, typeFilter); }
            else toast.error("TATA sync failed. Try again later.");
        } catch { toast.error("TATA sync failed. Try again later."); }
        finally { setResyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    /* ── Render ── */
    return (
        <Box sx={{ fontFamily: C.sans }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.txt1, fontFamily: C.sans }}>Contacts</Typography>
                    {!loading && (
                        <Box sx={{ px: 0.8, py: 0.15, borderRadius: 10, background: C.indigoSoft, border: `1px solid ${C.indigoBd}`, color: C.indigo, fontSize: 11, fontWeight: 700 }}>
                            {total}
                        </Box>
                    )}
                </Stack>
                <AddBtn startIcon={<Add sx={{ fontSize: 16 }} />} onClick={openAdd}>Add Contact</AddBtn>
            </Stack>

            {/* Filters */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                <SearchBox size="small" placeholder="Search by name or number…" value={searchInput}
                    onChange={e => setSearchInput(e.target.value)} sx={{ flex: 1, maxWidth: { sm: 320 } }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: C.txt4 }} /></InputAdornment>,
                        endAdornment: searchInput ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchInput("")}><Close sx={{ fontSize: 13, color: C.txt4 }} /></IconButton></InputAdornment> : null,
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontFamily: C.sans, fontSize: 13.5 }}>Type</InputLabel>
                    <StyledSelect label="Type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                        <MenuItem value="" sx={{ fontFamily: C.sans, fontSize: 13.5 }}>All</MenuItem>
                        {contactTypes.map(ct => (
                            <MenuItem key={ct.contact_type_id} value={ct.contact_type_id} sx={{ fontFamily: C.sans, fontSize: 13.5 }}>
                                {ct.contact_type}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </FormControl>
            </Stack>

            {/* Loading skeletons */}
            {loading && contacts.length === 0 && (
                <Stack spacing={1.25}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={isMobile ? 96 : 48} sx={{ borderRadius: 2 }} />
                    ))}
                </Stack>
            )}

            {/* Error */}
            {loadError && !loading && (
                <Box sx={{ py: 6, textAlign: "center" }}>
                    <ErrorOutline sx={{ fontSize: 36, color: C.red, mb: 1 }} />
                    <Typography sx={{ fontSize: 13.5, color: C.txt3, fontFamily: C.sans, mb: 1 }}>{loadError}</Typography>
                    <Button size="small" onClick={refresh} sx={{ textTransform: "none", fontFamily: C.sans, color: C.indigo, fontWeight: 600 }}>Retry</Button>
                </Box>
            )}

            {/* Empty */}
            {!loading && !loadError && contacts.length === 0 && (
                <Box sx={{ py: 8, textAlign: "center" }}>
                    <ContactPhone sx={{ fontSize: 40, color: C.border, mb: 1.5 }} />
                    <Typography sx={{ fontSize: 14, color: C.txt3, fontWeight: 500, fontFamily: C.sans }}>
                        {search || typeFilter ? "No contacts match your search." : "No contacts yet."}
                    </Typography>
                    {!search && !typeFilter && (
                        <Typography sx={{ fontSize: 12.5, color: C.txt4, fontFamily: C.sans, mt: 0.5 }}>
                            Click <strong>+ Add Contact</strong> to get started.
                        </Typography>
                    )}
                </Box>
            )}

            {/* Mobile cards */}
            {!loading && !loadError && contacts.length > 0 && isMobile && (
                <Box>
                    {contacts.map((c, i) => (
                        <ContactCard
                            key={c.contact_id ?? c.contactId ?? i}
                            c={c}
                            onCall={onCallNow}
                            onEdit={openEdit}
                            onDelete={confirmDelete}
                            onResync={handleResync}
                            resyncing={resyncingIds.has(c.contact_id ?? c.contactId)}
                        />
                    ))}
                </Box>
            )}

            {/* Desktop table */}
            {!loading && !loadError && contacts.length > 0 && !isMobile && (
                <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: 2, overflow: "hidden" }}>
                    <Table size="small" sx={{ fontFamily: C.sans }}>
                        <TableHead>
                            <TableRow sx={{ background: C.page }}>
                                {["Name", "Type", "Added On", "Actions"].map(h => (
                                    <TableCell key={h} sx={{ fontFamily: C.sans, fontWeight: 700, fontSize: 11.5, color: C.txt3, borderBottom: `1px solid ${C.border}`, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap", py: 1.25 }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contacts.map((c, i) => {
                                const id = c.contact_id ?? c.contactId;
                                const name = c.contact_name ?? c.contactName ?? "—";
                                const number = c.contact_number ?? c.contactNumber ?? "";
                                const typeName = c.ContactType?.contact_type ?? c.contactType ?? "—";
                                const synced = c.is_contact_sync ?? c.isContactSync ?? false;
                                const createdAt = c.created_at ?? c.createdAt;
                                const resyncing = resyncingIds.has(id);

                                return (
                                    <TableRow key={id ?? i} hover sx={{ "&:hover": { background: alpha(C.indigo, .02) }, transition: "background .1s" }}>
                                        <TableCell sx={{ borderBottom: `1px solid ${C.border2}`, py: 1.25 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 28, height: 28, background: avColor(name), fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                                                    {initials(name)}
                                                </Avatar>
                                                <Typography sx={{ fontFamily: C.sans, fontSize: 13, color: C.txt1, fontWeight: 600 }}>
                                                    {name}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${C.border2}`, py: 1.25 }}>
                                            <Box sx={{ display: "inline-flex", px: 1, py: 0.25, borderRadius: 6, background: C.indigoSoft, border: `1px solid ${C.indigoBd}`, fontSize: 11.5, fontWeight: 600, color: C.indigo, fontFamily: C.sans, whiteSpace: "nowrap" }}>
                                                {typeName}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3, borderBottom: `1px solid ${C.border2}`, py: 1.25, whiteSpace: "nowrap" }}>
                                            {fmtDate(createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${C.border2}`, py: 1.25 }}>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <CallBtn startIcon={<PhoneForwarded sx={{ fontSize: 12 }} />}
                                                    onClick={() => onCallNow?.({ leadDisplayId: "" }, { name, contactNumber: number })}>
                                                    Call
                                                </CallBtn>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => openEdit(c)}
                                                        sx={{ color: C.indigo, background: C.indigoSoft, border: `1px solid ${C.indigoBd}`, borderRadius: 6, width: 28, height: 28, "&:hover": { background: C.indigo, color: "#fff" } }}>
                                                        <Edit sx={{ fontSize: 13 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => confirmDelete(c)}
                                                        sx={{ color: C.red, background: C.redSoft, border: `1px solid ${C.redBd}`, borderRadius: 6, width: 28, height: 28, "&:hover": { background: C.red, color: "#fff" } }}>
                                                        <Delete sx={{ fontSize: 13 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {!synced && (
                                                    <Tooltip title="Resync to TATA">
                                                        <span>
                                                            <ResyncBtn disabled={resyncing}
                                                                startIcon={resyncing ? <CircularProgress size={10} sx={{ color: C.amber }} /> : <Sync sx={{ fontSize: 12 }} />}
                                                                onClick={() => handleResync(c)}>
                                                                Resync
                                                            </ResyncBtn>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                    <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded" size="small"
                        sx={{ "& .MuiPaginationItem-root": { fontFamily: C.sans, fontWeight: 500, fontSize: 13 }, "& .Mui-selected": { background: `${C.indigo} !important`, color: "#fff" } }}
                    />
                </Box>
            )}

            {/* Modals */}
            <ContactModal open={modal.open} mode={modal.mode} initialData={modal.initialData}
                contactTypes={contactTypes} onClose={() => setModal(p => ({ ...p, open: false }))}
                onSaved={handleSaved} userId={userId} />
            <DeleteDialog open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, contactId: null })}
                onConfirm={handleDelete} deleting={deleting} />
        </Box>
    );
}
